import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';
import { Star, ArrowLeft, MessageSquare, Users, Building2, TrendingUp, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';

const RatingStars = ({ value, size = 14 }) => {
  const v = Math.round(Number(value) || 0);
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`rating-stars-${v}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= v ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        />
      ))}
      <span className="ml-1.5 text-xs font-medium text-gray-600">{Number(value || 0).toFixed(1)}</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tint, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">{value ?? '—'}</p>
        </div>
        <div className={`p-2.5 rounded-lg ${tint} shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminRatings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [direction, setDirection] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, listRes] = await Promise.all([
        adminAPI.getRatingsSummary(),
        adminAPI.getAllRatings({ direction: direction === 'all' ? undefined : direction, limit: 100 }),
      ]);
      setSummary(summaryRes.data);
      setRatings(listRes.data.ratings || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ratings');
      setRatings([]);
    } finally {
      setLoading(false);
    }
  }, [direction]);

  useEffect(() => { load(); }, [load]);

  const talentSummary = summary?.client_to_talent || {};
  const clientSummary = summary?.talent_to_client || {};
  const talentTotal = talentSummary.total_ratings ?? 0;
  const talentAvg = talentSummary.average_rating;
  const clientTotal = clientSummary.total_ratings ?? 0;
  const clientAvg = clientSummary.average_client_rating;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100" data-testid="admin-ratings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/home')}
              className="shrink-0"
              data-testid="ratings-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">Ratings</h1>
              <p className="text-sm text-gray-500">Two-way feedback across clients and talent</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-[200px] bg-white" data-testid="ratings-direction-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ratings</SelectItem>
                <SelectItem value="client_to_talent">Client → Talent</SelectItem>
                <SelectItem value="talent_to_client">Talent → Client</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard
            icon={Users}
            label="Total Talent Ratings"
            value={talentTotal}
            tint="bg-cyan-50 text-cyan-600"
            testId="stat-total-talent"
          />
          <StatCard
            icon={Star}
            label="Avg Talent Score"
            value={talentAvg ? Number(talentAvg).toFixed(2) : '—'}
            tint="bg-amber-50 text-amber-600"
            testId="stat-avg-talent"
          />
          <StatCard
            icon={Building2}
            label="Total Client Ratings"
            value={clientTotal}
            tint="bg-emerald-50 text-emerald-600"
            testId="stat-total-client"
          />
          <StatCard
            icon={TrendingUp}
            label="Avg Client Score"
            value={clientAvg ? Number(clientAvg).toFixed(2) : '—'}
            tint="bg-purple-50 text-purple-600"
            testId="stat-avg-client"
          />
        </div>

        {/* Ratings list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">
              Recent ratings {ratings.length > 0 && <span className="text-gray-500 font-normal">({ratings.length})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ratings…
              </div>
            ) : ratings.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="ratings-empty-state">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No ratings yet for this filter.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="ratings-list">
                {ratings.map((r) => {
                  const isClientToTalent = r.direction === 'client_to_talent';
                  const score = isClientToTalent ? r.rating : r.client_rating;
                  const feedback = isClientToTalent ? r.feedback : r.client_feedback;
                  return (
                    <div
                      key={r.id}
                      className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 hover:border-cyan-300 transition-colors"
                      data-testid={`rating-row-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge
                              className={isClientToTalent
                                ? 'bg-cyan-100 text-cyan-700 border-0'
                                : 'bg-emerald-100 text-emerald-700 border-0'}
                            >
                              {isClientToTalent ? 'Client → Talent' : 'Talent → Client'}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              {r.created_at ? formatDateMMDDYYYY(r.created_at) : '—'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 break-words">
                            {isClientToTalent ? (
                              <><span className="text-gray-500">{r.client_company || 'Client'}</span> rated <strong>{r.talent_name}</strong></>
                            ) : (
                              <><strong>{r.talent_name}</strong> rated <span className="text-gray-500">{r.client_company || 'Client'}</span></>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 break-words">
                            {r.venue_name} · {r.gig_date ? formatDateMMDDYYYY(r.gig_date) : '—'}
                          </p>
                          {feedback && (
                            <p className="mt-2 text-sm text-gray-700 bg-gray-50 rounded p-2 border border-gray-100 break-words">
                              “{feedback}”
                            </p>
                          )}
                          {!isClientToTalent && r.would_work_again != null && (
                            <p className="text-xs text-gray-500 mt-2">
                              Would work again: <strong className={r.would_work_again ? 'text-emerald-600' : 'text-red-600'}>
                                {r.would_work_again ? 'Yes' : 'No'}
                              </strong>
                            </p>
                          )}
                        </div>
                        <div className="shrink-0">
                          <RatingStars value={score} size={16} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminRatings;
